import { useEffect, useRef, useState } from 'react'
import { MATCHS_PAR_APPEL, matchsPourAnnonces, type MatchAnnonce } from './apiMatching'

/**
 * Lesquelles des annonces affichées sont dans le fil « Recommandé pour vous » — ticket
 * `fo-recherche-16`.
 *
 * **Différé et incrémental.** Le listing s'affiche d'abord ; l'étiquette se demande ensuite, pour
 * les seuls identifiants qu'on n'a pas encore demandés — six à la première page, six à chaque
 * « voir plus ». Une réponse en retard ne retarde rien, et un échec ne casse rien : sans
 * étiquette, la carte est simplement la carte. Les identifiants d'un appel échoué sont rendus,
 * pour qu'une page suivante les redemande.
 *
 * Vide et inerte pour un visiteur : la question « est-ce dans mon fil ? » n'a pas de sens sans
 * compte, et la poser produirait un 401 par page. À la déconnexion, tout est oublié, réponses en
 * vol comprises — une étiquette calculée pour un autre compte est exactement ce que l'invariant 17
 * interdit de resservir.
 */
export function useRecommandees(
  ids: readonly string[],
  connecte: boolean,
): ReadonlyMap<string, MatchAnnonce> {
  const [carte, setCarte] = useState<ReadonlyMap<string, MatchAnnonce>>(() => new Map())
  const demandees = useRef(new Set<string>())
  // Le numéro de la session en cours : une réponse arrivée après une déconnexion est ignorée.
  const session = useRef(0)

  useEffect(() => {
    if (connecte) {
      return
    }

    session.current += 1
    demandees.current = new Set()
    setCarte(new Map())
  }, [connecte])

  useEffect(() => {
    if (!connecte) {
      return
    }

    const nouvelles = ids.filter((id) => !demandees.current.has(id))

    if (nouvelles.length === 0) {
      return
    }

    for (const id of nouvelles) {
      demandees.current.add(id)
    }

    const ouverte = session.current

    for (let debut = 0; debut < nouvelles.length; debut += MATCHS_PAR_APPEL) {
      const tranche = nouvelles.slice(debut, debut + MATCHS_PAR_APPEL)

      void matchsPourAnnonces(tranche)
        .then((matchs) => {
          if (ouverte !== session.current || matchs.length === 0) {
            return
          }

          setCarte((precedente) => {
            const suivante = new Map(precedente)

            for (const match of matchs) {
              suivante.set(match.listingId, match)
            }

            return suivante
          })
        })
        .catch(() => {
          if (ouverte === session.current) {
            for (const id of tranche) {
              demandees.current.delete(id)
            }
          }
        })
    }
  }, [ids, connecte])

  return carte
}
